import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Col,
    Row,
    FormRadioGroup,
    InPageAlert
} from '@snsw/react-component-library';
import styled from 'styled-components';
import {
    ActionType,
    ImpactedAddressMode,
    PersonalDisasterEligibility,
    useApplicationConfig
} from '../../store';
import { useApplicationContext } from '../../store';
import { ImpactedAddress } from '../ImpactedAddress';
import { ErrorSummaryType } from '../EligibilityPageProjectArchetype';
import DebugEligibilitySummary from '../Debug/DebugEligibilitySummary';
import DebugNote from '../Debug/DebugNote';
import { Heading } from '@snsw/react-component-library';

// const LabelWrapper = styled.div`
//   display: block;
//   color: #242934;
//   margin-bottom: -1rem;
//   font-weight: 500;
//   line-height: 1.5;
//   margin-top: 1.5rem;
// `;
//This could probably be removed, Kiama library recommends questions and their associated sub information are put in
//legend and helpMessage instead

const RadioWrapper = styled.div`
  margin-top: 2rem;
`;

const livingAtAddressOptions = [
    { value: 'only person', label: 'I’m the only person living at the impacted address ' },
    {
        value: 'living with others',
        label: 'I live with others at the impacted address (This includes living with children)'
    }
];

export const PersonalDisasterEligibilityPage = ({ errors, showErrors }: PersonalDisasterEligibilityPageProps) => {
    const appConfig = useApplicationConfig();
    const { globalState, dispatch } = useApplicationContext();
    const eligibility: PersonalDisasterEligibility = globalState.eligibility;
    const profileConnectInfo = globalState.profileConnectInfo;
    const [isCustomAddress, setIsCustomAddress] = useState(eligibility.impactedAddressSource === 'other');
    const [mode, setMode] = useState<ImpactedAddressMode>('auto');
    const defaultErrorMessage = 'This field is required.';
    const profileConnectAddress = (profileConnectInfo?.residentialAddress?.formattedAddress &&
        profileConnectInfo?.residentialAddress?.formattedAddress !== '') ?
        profileConnectInfo?.residentialAddress?.formattedAddress : 'no residential address';

    useEffect(() => {
        eligibility['impactedAddressMode'] = mode;
    }, []);

    const onChangeEvent = (fieldName: string) => value => {
        eligibility[fieldName] = value;
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility } });
    };

    const onSwitchMode = (mode: ImpactedAddressMode) => {
        eligibility['impactedAddressMode'] = mode;
        setMode(mode);
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility } });
    };

    const onChangeAddressSource = fieldName => value => {
        const changeEvent = onChangeEvent(fieldName);
        changeEvent(value);
        setIsCustomAddress(value !== 'residential');
    };

    const isError = (fieldName: string) => typeof errors === 'object' &&
        !!errors[fieldName] &&
        showErrors;

    const errorMessage = (fieldName: string) => (typeof errors === 'object' &&
        (errors[fieldName]?.message ?? defaultErrorMessage)) ||
        '';

    return (
        <>
            <Heading level='2'>Eligibility</Heading>
            <Row>
                <Col span={6}>
                    <RadioWrapper>
                        <FormRadioGroup
                            id='ownerOrRenter'
                            name='ownerOrRenter'
                            vertical={true}
                            legend='Do you own or rent the property that’s been impacted?'
                            options={[
                                { value: 'owner', label: 'I own the property' },
                                { value: 'renter', label: 'I rent the property' }
                            ]}
                            onChange={onChangeEvent('ownerOrRenter')}
                            value={eligibility.ownerOrRenter}
                            hasError={isError('ownerOrRenter')}
                            errorMessage={errorMessage('ownerOrRenter')}
                        />
                    </RadioWrapper>
                </Col>
            </Row>

            {
                eligibility.ownerOrRenter === 'owner' &&
                <Row>
                    <Col span={6}>
                        <RadioWrapper>
                            <FormRadioGroup
                                id='ownerOccupierOrInvestor'
                                name='ownerOccupierOrInvestor'
                                vertical={true}
                                legend='Are you an owner occupier or investor?'
                                options={[
                                    { value: 'owner occupier', label: 'I\'m an owner occupier' },
                                    { value: 'investor', label: 'I\'m an investor' }
                                ]}
                                onChange={onChangeEvent('ownerOccupierOrInvestor')}
                                value={eligibility.ownerOccupierOrInvestor}
                                hasError={isError('ownerOccupierOrInvestor')}
                                errorMessage={errorMessage('ownerOccupierOrInvestor')}
                                helpMessage='If your property is tenanted, select ‘I’m an investor’'
                            />
                        </RadioWrapper>
                    </Col>
                </Row>
            }
            {
                eligibility.ownerOrRenter === 'renter' &&
                <Row>
                    <Col span={6}>
                        <RadioWrapper>
                            <FormRadioGroup
                                id='peopleLivingAtAddress'
                                name='peopleLivingAtAddress'
                                vertical={true}
                                legend='Are you the only person living at the impacted address?'
                                options={livingAtAddressOptions}
                                onChange={onChangeEvent('peopleLivingAtAddress')}
                                value={eligibility.peopleLivingAtAddress}
                                hasError={isError('peopleLivingAtAddress')}
                                errorMessage={errorMessage('peopleLivingAtAddress')}
                            />
                        </RadioWrapper>
                        {
                            eligibility.peopleLivingAtAddress === 'living with others' &&
                            <RadioWrapper>
                                <InPageAlert
                                    variant='info'
                                    title='Important information'
                                >
                                    Only one {appConfig.productTitle} application can be assessed for your address.
                                    You’ll need to get permission from tenants living with you,
                                    or authority to act on their behalf.
                                </InPageAlert>
                            </RadioWrapper>
                        }
                    </Col>
                </Row>
            }
            <Row>
                <Col span={6}>
                    <RadioWrapper>
                        <FormRadioGroup
                            id='impactedAddressSource'
                            name='impactedAddressSource'
                            vertical={true}
                            legend='Address of the impacted property'
                            options={[
                                { value: 'residential', label: profileConnectAddress },
                                { value: 'other', label: 'Other' }
                            ]}
                            value={eligibility.impactedAddressSource}
                            onChange={onChangeAddressSource('impactedAddressSource')}
                            hasError={isError('impactedAddressSource')}
                            errorMessage={errorMessage('impactedAddressSource')}
                        />
                    </RadioWrapper>
                </Col>
            </Row>
            {isCustomAddress ?
                <>
                    <DebugNote>
                        (Storybook only) Regardless of which auto-suggested address is selected below, the mock acts as if MCKELL
                        BUILDING was selected (see the Mock Request tab).
                    </DebugNote>
                    <Row>
                        <Col span={6}>
                            <ImpactedAddress
                                apiOptions={{
                                    key: appConfig.apiKeys.address,
                                    outFields: ['lgaName', 'lgaShortName', 'lgaPid']
                                }}
                                initialMode={mode}
                                initialValue={eligibility.impactedAddress ?? {}}
                                onChangeEvent={onChangeEvent('impactedAddress')}
                                onSwitch={onSwitchMode}
                                showErrors={showErrors}
                            />
                        </Col>
                    </Row>
                </> :
                <Row />
            }
            <Row>
                <Col span={6}>
                    <RadioWrapper>
                        <FormRadioGroup
                            id='insurance'
                            name='insurance'
                            vertical={true}
                            legend='Do you have insurance that covers all costs to repair your home and/or replace contents?'
                            options={[
                                { value: 'covered', label: 'Yes, my insurance can cover all costs' },
                                { value: 'not insured', label: 'No, I’m not fully insured' }
                            ]}
                            onChange={onChangeEvent('insurance')}
                            value={eligibility.insurance}
                            hasError={isError('insurance')}
                            errorMessage={errorMessage('insurance')}
                        />
                    </RadioWrapper>
                </Col>
            </Row>

            {eligibility.insurance === 'covered' && <InPageAlert
                variant='error'
                title='We cannot progress your application '
            >
                The {appConfig.productTitle} provides support to people who are not fully insured for property damage or
                household contents. Check the eligibility guidelines.
            </InPageAlert>}

            <DebugEligibilitySummary
                pageName='personal disaster eligibility'
                componentName='PersonalDisasterEligibilityPage'
                eligibility={eligibility}
                errors={errors}
                showErrors={showErrors}
            />
        </>
    );
};

interface PersonalDisasterEligibilityPageProps {
    errors: ErrorSummaryType,
    showErrors: boolean
}

PersonalDisasterEligibilityPage.propTypes = {
    errors: PropTypes.object,
    showErrors: PropTypes.bool
};
